import {
  Box,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import { DialogCloseButton } from 'components/common/DialogCloseButton';
import { TrackList } from 'components/library/TrackList';
import { TrackEntry } from 'components/track/TrackEntry';
import { TrackMapToggleButton } from 'components/track/TrackMapToggleButton';
import { useLocalDatabase } from 'hooks/useLocalDatabase';
import { useTrackMapping } from 'hooks/useTrackMapping';
import { useUser } from 'hooks/useUser';
import { LocalTrack } from 'models/LocalTrack';
import { Track } from 'models/Track';
import { TrackRequest, TrackRequestType } from 'models/TrackRequest';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface MapTrackDialogProps {
  open: boolean;
  request: TrackRequest;
  // mappedTrack?: LocalTrack;
  onAddMapping: (localTrack: LocalTrack, rememberSelection: boolean) => void;
  onRemoveMapping: (localTrack: LocalTrack, rememberSelection: boolean) => void;
  onClose?: (ev: React.MouseEvent) => void;
}

export function MapTrackDialog(props: MapTrackDialogProps) {
  const { open, request, onAddMapping, onRemoveMapping, onClose } = props;
  const { t } = useTranslation();
  const [mappedTrack, setMappedTrack] = useState<LocalTrack | null>(request.localTrack || null);
  const [isAutoMapped, setIsAutoMapped] = useState(request.autoMapped || false);
  const [localTracks, setLocalTracks] = useState<LocalTrack[]>(
    request.localTrack ? [request.localTrack] : []
  );
  const { localDatabase, localDatabaseLoaded } = useLocalDatabase();
  const { preferences, updatePreferences } = useUser();
  const { getFindMatchThreshold } = useTrackMapping();

  const isTrackLike =
    request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;

  const [searchArtist, setSearchArtist] = useState(
    isTrackLike ? (request.value as Track).artist : ''
  );

  const [searchTitle, setSearchTitle] = useState(
    isTrackLike
      ? (request.value as Track).title
      : (request.value as string)
  );

  const [rememberRequestMapping, setRememberRequestMapping] = useState(
    preferences.defaultRememberRequestMapping || false
  );

  const handleCloseDialog = (event: React.MouseEvent) => {
    onClose?.(event);
  };

  const handleAddRequestMapping = (selectedTrack: LocalTrack) => {
    onAddMapping(selectedTrack, rememberRequestMapping);
    setMappedTrack(selectedTrack);
    setIsAutoMapped(false);
  };

  const handleRemoveRequestMapping = (selectedTrack: LocalTrack) => {
    onRemoveMapping(selectedTrack, rememberRequestMapping);
    setMappedTrack(null);
    setIsAutoMapped(false);
  };

  const findTracks = async () => {
    let foundTracks: LocalTrack[];
    // let cleanedValues: { artist: string; title: string } | undefined;

    if (!localDatabaseLoaded || (searchArtist === '' && searchTitle === '')) {
      return;
    }

    foundTracks =
      (await localDatabase?.findTracks({
        title: searchTitle.trim(),
        artist: searchArtist.trim(),
        maxDistance: getFindMatchThreshold(),
        // Capped to 99 to avoid badge UI issues
        limit: 99,
      })) || [];

    // Add mapped track, if it exists and is not already in the foundTracks list
    if (mappedTrack && !foundTracks.find((track) => track.id === mappedTrack.id)) {
      foundTracks.unshift(mappedTrack);
    }

    setLocalTracks(foundTracks);
  };

  useEffect(() => {
    // const findTracks = async () => {
    //   let tracks: LocalTrack[] | undefined;
    //   let trimmedValues: { artist: string; title: string } | undefined;

    //   // First, try searching with the original artist and title
    //   if (searchArtist === "" && searchTitle === "") {
    //     setLocalTracks([] as LocalTrack[]);
    //   } else {
    //     tracks = await localDatabase?.findTracks(searchTitle.trim(), searchArtist.trim());
    //   }

    //   console.log('t', tracks)

    //   // If no tracks found, try searching with trimmed artist and title
    //   if (!tracks || tracks?.length === 0 && searchArtist !== '' && searchTitle !== '' && typeof request.value !== 'string') {
    //     trimmedValues = cleanArtistTitlecleanArtistTitle(request.value as ITrack);
    //     tracks = await localDatabase?.findTracks(trimmedValues.title, trimmedValues.artist);
    //   }

    //   if (tracks) {
    //     setLocalTracks(tracks);
    //     console.log(3);
    //   }

    //   // Update the search fields with the trimmed values if they were used
    //   if (trimmedValues) {
    //     setSearchArtist(trimmedValues.artist);
    //     setSearchTitle(trimmedValues.title);
    //     // setFirstCall(false);
    //   }
    // };

    findTracks();
  }, [localDatabase, localDatabaseLoaded, searchArtist, searchTitle]);

  useEffect(() => {
    if (mappedTrack) {
      setLocalTracks((prevTracks) =>
        prevTracks.map((track) => (track.id === mappedTrack.id ? mappedTrack : track))
      );
    }
  }, [mappedTrack]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth>
      <DialogTitle variant="h3" textAlign="center">
        {t('findTrackInYourLibrary', 'Find track in your library')}
      </DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent sx={{ pb: 0, textAlign: 'center' }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            marginBottom: '1rem',
            display: 'inline-block',
            textAlign: 'start',
            minWidth: '240px',
          }}
        >
          <TrackEntry
            artist={isTrackLike ? (request.value as Track).artist : ''}
            title={
              isTrackLike
                ? (request.value as Track).title
                : (request.value as string)
            }
            cover={
              isTrackLike
                ? (request.value as Track).artworkUrl
                : undefined
            }
          />
        </Card>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Title"
              fullWidth
              size="medium"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t('artist', 'Artist')}
              fullWidth
              size="medium"
              value={searchArtist}
              onChange={(e) => setSearchArtist(e.target.value)}
            />
          </Grid2>
        </Grid2>
        <Box display="flex" justifyContent="flex-end">
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberRequestMapping}
                onChange={() => {
                  updatePreferences({ defaultRememberRequestMapping: !rememberRequestMapping });
                  setRememberRequestMapping(!rememberRequestMapping);
                }}
              />
            }
            label={t('rememberSelectionForFutureEvents', 'Remember selection for future events')}
            sx={{ margin: 0 }}
          />
        </Box>
      </DialogContent>
      <DialogContent dividers>
        <Box sx={{ height: 320 }}>
          <TrackList
            trackList={localTracks || []}
            highlightRow={(row) => row.id == mappedTrack?.id}
            actions={(params) => {
              const isMapped = params.row.id == mappedTrack?.id;

              return (
                <TrackMapToggleButton
                  isMapped={isMapped}
                  isAutoMapped={isMapped && isAutoMapped}
                  onClick={() =>
                    isMapped
                      ? handleRemoveRequestMapping(params.row)
                      : handleAddRequestMapping(params.row)
                  }
                />
              );
            }}
            // actions={(params) => {
            //   if (params.row.id == mappedTrack?.id) {
            //     return (
            //       <Tooltip
            //         title={t('removeRequestMapping', 'Remove this track from selected request')}
            //       >
            //         <IconButton
            //           aria-label="map"
            //           onClick={() => handleRemoveRequestMapping(params.row)}
            //         >
            //           <CheckCircleIcon color="primary" />
            //         </IconButton>
            //       </Tooltip>
            //     );
            //   }

            //   return (
            //     <Tooltip
            //       title={t('addRequestMapping', 'Assign this track to the selected request')}
            //     >
            //       <IconButton aria-label="map" onClick={() => handleAddRequestMapping(params.row)}>
            //         <CheckCircleOutlineIcon />
            //       </IconButton>
            //     </Tooltip>
            //   );
            // }}
          />
          {/* <TrackList
            trackList={localTracks || []}
            highlightRow={(row) => row.id == request.localId}
            actions={(params) => {
              if (params.row.id == request.localId) {
                // return <CheckCircleIcon color="primary" />;
                return (
                  <Tooltip
                    title={t('removeRequestMapping', 'Remove this track from selected request')}
                  >
                    <IconButton
                      aria-label="map"
                      onClick={() => handleRemoveRequestMapping(params.row)}
                    >
                      <CheckCircleIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                );
              }

              return (
                <Tooltip
                  title={t('addRequestMapping', 'Assign this track to the selected request')}
                >
                  <IconButton aria-label="map" onClick={() => handleAddRequestMapping(params.row)}>
                    <CheckCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              );
            }}
          /> */}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>{t('close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
